import { ReactNode } from 'react';

/**
 * ASP.NET ProblemDetails shape (RFC 7807)
 */
interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  extensions?: Record<string, unknown>;
}

/**
 * ASP.NET ValidationProblemDetails extends ProblemDetails with errors map
 */
interface ValidationProblemDetails extends ProblemDetails {
  errors?: Record<string, string[]>;
}

export default class HttpErrorHelper {
  /**
   * Type guard for ValidationProblemDetails (has errors map)
   */
  private static isValidationProblemDetails(data: unknown): data is ValidationProblemDetails {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    // ValidationProblemDetails has an errors object (not array)
    return obj.errors !== undefined && typeof obj.errors === 'object' && !Array.isArray(obj.errors);
  }

  /**
   * Type guard for ProblemDetails (has title or detail, but no errors)
   */
  private static isProblemDetails(data: unknown): data is ProblemDetails {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    return typeof obj.title === 'string' || typeof obj.detail === 'string';
  }

  /**
   * Converts a value to an array of strings
   */
  private static toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((v) => typeof v === 'string');
    }
    if (typeof value === 'string') {
      return [value];
    }
    return [];
  }

  /**
   * function handleErrorMessage
   *
   * Used to handle and correctly interpret errors, mainly through try/catch
   * @param msg received error object
   * @returns array of errors
   */
  private static handleErrorMessage = (msg: any): string | { key: string; errors: string[] }[] => {
    const arr: { key: string; errors: string[] }[] = [];

    if (msg?.response?.status === 400) {
      const data = msg.response.data;

      // Handle ASP.NET ValidationProblemDetails (errors is a map of field -> string[])
      if (this.isValidationProblemDetails(data) && data.errors) {
        // First add DomainValidations as global errors (most important)
        if (data.errors['DomainValidations']) {
          const domainErrors = this.toStringArray(data.errors['DomainValidations']);
          if (domainErrors.length > 0) {
            arr.push({ key: '', errors: domainErrors });
          }
        }

        // Then add field-specific validation errors
        for (const [key, value] of Object.entries(data.errors)) {
          if (key === 'DomainValidations') continue; // Already handled above
          const errors = this.toStringArray(value);
          if (errors.length > 0) {
            arr.push({ key, errors });
          }
        }

        // If we got errors, return them
        if (arr.length > 0) {
          return arr;
        }
      }

      // Handle plain ProblemDetails (no errors map, just title/detail)
      if (this.isProblemDetails(data)) {
        const message = data.detail || data.title || 'Valideringsfeil';
        return message;
      }

      // Fallback for unknown 400 response shape
      if (typeof data === 'string' && data.length > 0) {
        return data;
      }

      return 'Valideringsfeil oppsto';
    } else if (msg?.response) {
      const status = msg.response.status;

      // Spesifikke feilmeldinger for vanlige HTTP-statuskoder
      if (status === 429 || status === 405) {
        return 'For mange forespørsler. Vennligst vent litt og prøv igjen.';
      }
      if (status === 503) {
        return 'Tjenesten er midlertidig utilgjengelig. Prøv igjen senere.';
      }
      if (status === 504) {
        return 'Forespørselen tok for lang tid. Prøv igjen senere.';
      }
      if (status === 401) {
        return 'Du er ikke logget inn. Vennligst logg inn på nytt.';
      }
      if (status === 403) {
        return 'Du har ikke tilgang til denne ressursen.';
      }
      if (status === 404) {
        return 'Ressursen ble ikke funnet.';
      }
      if (status >= 500) {
        return 'En serverfeil oppsto. Prøv igjen senere.';
      }

      const statusText =
        msg.response.statusText ||
        msg.response.data?.title ||
        msg.response.data?.message ||
        'Ukjent serverfeil';
      const statusCode = status ? ` (${status})` : '';
      return `${statusText}${statusCode}`;
    } else if (msg && msg.message) {
      // Network error, timeout, or thrown Error without response
      return `Nettverksfeil: ${msg.message}`;
    } else {
      return 'Ukjent feil oppsto';
    }

    return arr;
  };

  public static formatError(msg: any) {
    return this.formatErrorObject(this.handleErrorMessage(msg));
  }

  private static formatErrorObject(
    error:
      | string
      | {
          key: string;
          errors: string[];
        }[],
  ): ReactNode {
    if (typeof error === 'string') {
      return error;
    }
    const items: { key?: string; msg: string }[] = [];
    error.forEach((o) => {
      o.errors.forEach((e) => items.push({ key: o.key, msg: e }));
    });
    return (
      <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
        {items.map((i, idx) => (
          <li key={idx}>
            {i.key && i.key !== 'DomainValidations' ? (
              <>
                <strong>{i.key}:</strong> {i.msg}
              </>
            ) : (
              i.msg
            )}
          </li>
        ))}
      </ul>
    );
  }
}
